import { Action } from 'widgets-for-react'
import { Map, Set } from 'immutable'
import { validateField } from './newsletterFormBlockValidation'

export interface NewsletterFormBlockState {
  data: FormState
  visited: Set<Field>
  validationMessages: Map<Field, string>
  status: 'initial' | 'submitting' | 'succeeded' | 'failed'
}

export const genderValues = ['V', 'M'] as const
type GenderValue = typeof genderValues[number]

export const genderLabels: { [n in GenderValue]: string } = {
  V: 'Vrouw',
  M: 'Man',
}

export const newslettersId = [ 16, 18, 19, 20 ] as const

export type NewsletterId = typeof newslettersId[number];

export const newsletterName: { [n in NewsletterId]: string } = {
  16: 'Algemene nieuwsbrief Bibliotheek Hengelo',
  18: 'Algemene nieuwsbrief Bibliotheek Hof van Twente',
  19: 'Algemene nieuwsbrief Bibliotheek Oldenzaal',
  20: 'Nieuwsbrief de Bibliotheek op school'
}

export const newsletterLabel: { [n in NewsletterId]: string } = {
  16: 'Ja, ik wil de nieuwsbrief van Bibliotheek Hengelo met leestips, praktische informatie, online diensten en activiteiten ontvangen.',
  18: 'Ja, ik wil de nieuwsbrief van Bibliotheek Hof van Twente met leestips, praktische informatie, online diensten en activiteiten ontvangen.',
  19: 'Ja, ik wil de nieuwsbrief van Bibliotheek Oldenzaal met leestips, praktische informatie, online diensten en activiteiten ontvangen.',
  20: 'Ja, ik ontvang de nieuwsbrief over de Bibliotheek op school met praktische tips en informatie voor docenten en geïnteresseerden.'
}

export interface FormState {
  emailAddress: string
	firstName: string
  lastName: string
  birthDate: Date
  newsletters: Set<NewsletterId>
}

export type Field = keyof FormState

export const initialFormState: FormState = {
  emailAddress: '',
  firstName: '',
  lastName: '',
  birthDate: undefined,
  newsletters: Set<NewsletterId>()
}

export const initialBirthDate: Date = new Date()
initialBirthDate.setFullYear(initialBirthDate.getFullYear() - 30)

export const allFields = Object.keys(initialFormState) as Array<Field>

export const initialState: NewsletterFormBlockState = {
  data: initialFormState,
  visited: Set<Field>(),
  validationMessages: Map<Field, string>(),
  status: 'initial'
}

export const setFieldOnState =
  (setState: (action: Action<NewsletterFormBlockState>) => void) =>
    <Field extends keyof FormState>(field: Field) =>
      (value: FormState[Field]) =>
        setState((s) => ({
          ...s, data: { ...s.data, [field]: value }
        }))

export const toggleNewsletterOnState =
  (setState: (action: Action<NewsletterFormBlockState>) => void) =>
    (value: NewsletterId) =>
      setState((s) => ({
        ...s, data: {
          ...s.data,
          newsletters: s.data.newsletters.has(value)
            ? s.data.newsletters.remove(value)
            : s.data.newsletters.add(value)
        }
      }))

export const setVisited =
  (setState: (action: Action<NewsletterFormBlockState>) => void) => (field: Field) =>
    setState((s) => ({ ...s, visited: s.visited.add(field) }))

export const validateFormData =
  (setState: (action: Action<NewsletterFormBlockState>) => void) =>
    (all?: boolean) => setState((prevState) => {
      const fields = all ? allFields : prevState.visited.toArray()

      const validationMessages = validateFields(fields, prevState.data)

      // When "all fields" is triggered once, ensure all is validated on updates
      const visited = Set(fields)

      return { ...prevState, validationMessages, visited }
    })

export const validateFields = (fields: Field[], data: FormState) => fields.reduce<Map<Field, string>>((messages, field) => {
  const result = validateField(field)(data[field])
  return result.status == 'invalid'
    ? messages.set(field, result.message)
    : messages
}, Map<Field, string>())


